/**
 * Map BaggageViewModel[] → Details presentation chips/copy.
 * No raw parsing. Live copy rules only.
 */

import { BAGGAGE_STATUS } from "../view-models/types";

/**
 * @param {import("../view-models/types").BaggageViewModel[]|null|undefined} baggage
 * @param {{ cabin?: string|null, fareType?: string|null }} [meta]
 * @returns {{ chips: { label: string, tone: string }[], detailChips: { label: string, tone: string }[], summaryLines: string[] }}
 */
export function mapDetailsBaggageDisplay(baggage, meta = {}) {
  const rows = Array.isArray(baggage) ? baggage : [];
  const chips = [];
  const summaryLines = [];

  const allUnknown =
    rows.length === 0 ||
    rows.every((r) => !r?.status || r.status === BAGGAGE_STATUS.UNKNOWN);

  if (allUnknown) {
    const chip = { label: "פרטי כבודה לא זמינים", tone: "muted" };
    return {
      chips: [chip],
      detailChips: [chip],
      summaryLines: [chip.label],
    };
  }

  for (const b of rows) {
    if (!b?.status || b.status === BAGGAGE_STATUS.UNKNOWN) continue;

    if (b.status === BAGGAGE_STATUS.INCLUDED) {
      const label = includedLabel(b);
      if (label) {
        chips.push({ label, tone: "included" });
        summaryLines.push(label);
      }
      continue;
    }

    if (b.status === BAGGAGE_STATUS.AVAILABLE_FOR_PURCHASE) {
      chips.push({ label: "ניתן להוסיף בתשלום", tone: "paid" });
      summaryLines.push("ניתן להוסיף בתשלום");
      continue;
    }

    if (b.status === BAGGAGE_STATUS.SELECTED_PAID) {
      const label = b.label
        ? `נבחר: ${b.label}`
        : "כבודה נבחרה";
      chips.push({ label, tone: "paid" });
      summaryLines.push(label);
      continue;
    }

    if (b.status === BAGGAGE_STATUS.NOT_INCLUDED) {
      chips.push({ label: "ללא כבודה", tone: "muted" });
      summaryLines.push("ללא כבודה");
      continue;
    }

    if (b.status === BAGGAGE_STATUS.UNAVAILABLE) {
      chips.push({ label: "כבודה לא זמינה", tone: "muted" });
      summaryLines.push("כבודה לא זמינה");
    }
  }

  if (chips.length === 0) {
    const chip = { label: "פרטי כבודה לא זמינים", tone: "muted" };
    return {
      chips: [chip],
      detailChips: [chip],
      summaryLines: [chip.label],
    };
  }

  const unique = dedupeChips(chips);
  const detailChips = [...unique];
  if (meta.cabin) {
    detailChips.push({ label: `מחלקת ${meta.cabin}`, tone: "info" });
  }
  if (meta.fareType) {
    detailChips.push({ label: String(meta.fareType), tone: "info" });
  }

  return {
    chips: unique,
    detailChips,
    summaryLines: [...new Set(summaryLines)],
  };
}

function includedLabel(b) {
  if (b.label && String(b.label).trim()) return String(b.label).trim();
  if (b.kind === "carryOn") {
    const q = Number(b.quantity);
    return q > 1 ? `${q} טרולי כלולים` : "טרולי כלול";
  }
  if (b.kind === "checked") {
    const q = Number(b.quantity);
    if (b.weightKg != null) {
      return q > 1
        ? `${q} מזוודות (${b.weightKg} ק״ג)`
        : `מזוודה כלולה (${b.weightKg} ק״ג)`;
    }
    return q > 1 ? `${q} מזוודות כלולות` : "מזוודה כלולה";
  }
  return "כבודה כלולה";
}

function dedupeChips(chips) {
  const seen = new Set();
  const out = [];
  for (const c of chips) {
    const key = `${c.tone}|${c.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}
